import { updateGroupPermission } from '@app/modules/admin/api/permissions';
import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import { GroupPermissionSource, GroupPermissionState } from '@app/modules/admin/types';
import { useToast } from '@app/core/notifications';
import { Box, Card, CardContent } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { reportError } from '../../../../core/integrations/bugsnag';

interface Props {
  readonly permissionState: GroupPermissionState;
  readonly stationId: string;
  readonly onUpdate?: (updatedState: GroupPermissionState) => void;
}

export function StationGroupPermissionItem(p: Props) {
  const { permissionState: initialPermissionState, stationId, onUpdate } = p;
  const [permissionState, setPermissionState] = useState(initialPermissionState);
  const [isUpdating, setIsUpdating] = useState(false);
  const { $t } = useIntl();
  const { notifyError } = useToast();

  const hasPermission = permissionState.permission !== null;

  const handleToggle = async (enabled: boolean) => {
    if (isUpdating) {
      return;
    }

    setIsUpdating(true);
    try {
      await updateGroupPermission(stationId, {
        groupId: permissionState.group.id,
        allowed: enabled,
      });

      const updatedState: GroupPermissionState = {
        ...permissionState,
        permission: enabled
          ? {
              type: GroupPermissionSource.DIRECT,
            }
          : null,
      };

      setPermissionState(updatedState);
      onUpdate?.(updatedState);
    } catch (err) {
      reportError(err as Error);
      notifyError(
        $t({
          id: 'admin.stations.viewScreen.permissionUpdateError',
          defaultMessage: 'No se pudo actualizar el permiso',
        }),
      );
      // On error, revert to original state
      setPermissionState(initialPermissionState);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <StatusSwitch
            checked={hasPermission}
            onChange={(event) => {
              handleToggle(event.target.checked);
            }}
            disabled={isUpdating}
            sx={{ marginBottom: 0 }}
          />
          <Box>{permissionState.group.name}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}
